import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Building, CheckCircle2, RefreshCw, AlertCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const LinkedInPageSelectPage = () => {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selecting, setSelecting] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Get token from URL params (passed from backend after OAuth)
  const params = new URLSearchParams(window.location.search);
  const tempToken = params.get("token");

  useEffect(() => {
    const fetchOrganizations = async () => {
      if (!tempToken) {
        setError("No authentication token provided");
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/integrations/linkedin/organizations-popup`, {
          params: { token: tempToken },
        });
        setOrganizations(response.data.organizations || []);
      } catch (err) {
        console.error("Error fetching organizations:", err);
        setError(err.response?.data?.detail || "Failed to fetch company pages");
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizations();
  }, [tempToken]);

  const selectOrganization = async (orgId, orgName) => {
    setSelecting(orgId);
    try {
      await axios.post(
        `${API}/integrations/linkedin/select-organization-popup`,
        { organization_id: orgId },
        { params: { token: tempToken } }
      );
      setSuccess(true);
      
      // Close popup after 2 seconds
      setTimeout(() => {
        if (window.opener) {
          window.opener.location.href = window.opener.location.pathname + "?linkedin=connected";
          window.close();
        } else {
          window.location.href = "/app/admin?linkedin=connected";
        }
      }, 1500);
    } catch (err) {
      console.error("Error selecting organization:", err);
      setError(err.response?.data?.detail || "Failed to select company page");
      setSelecting(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <RefreshCw className="w-8 h-8 text-[#0A66C2] animate-spin mx-auto mb-4" />
              <p className="text-slate-600">Loading your company pages...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-slate-800 mb-2">Connected!</h2>
              <p className="text-slate-600">Closing this window...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-slate-800 mb-2">Error</h2>
              <p className="text-slate-600 mb-4">{error}</p>
              <Button onClick={() => window.close()} variant="outline">
                Close Window
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-12 h-12 rounded-lg bg-[#0A66C2] flex items-center justify-center mx-auto mb-3">
            <svg className="w-7 h-7" viewBox="0 0 24 24" fill="white">
              <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19a.66.66 0 000 .14V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z"/>
            </svg>
          </div>
          <CardTitle>Select Company Page</CardTitle>
          <CardDescription>
            Choose which LinkedIn company page to connect for analytics
          </CardDescription>
        </CardHeader>
        <CardContent>
          {organizations.length === 0 ? (
            <div className="text-center py-6">
              <Building className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600 mb-2">No company pages found</p>
              <p className="text-sm text-slate-500">
                Make sure you are an admin of a LinkedIn company page.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {organizations.map((org) => (
                <button
                  key={org.id}
                  onClick={() => selectOrganization(org.id, org.name)}
                  disabled={selecting !== null}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                    selecting === org.id
                      ? "border-[#0A66C2] bg-blue-50"
                      : "border-slate-200 hover:border-[#0A66C2] hover:bg-slate-50"
                  } ${selecting !== null && selecting !== org.id ? "opacity-50" : ""}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                      <Building className="w-5 h-5 text-slate-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-slate-800">{org.name}</p>
                      {org.vanity_name && (
                        <p className="text-sm text-slate-500">linkedin.com/company/{org.vanity_name}</p>
                      )}
                    </div>
                    {selecting === org.id && (
                      <RefreshCw className="w-5 h-5 text-[#0A66C2] animate-spin" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LinkedInPageSelectPage;
